import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { ApiErrorResponse, ErrorHandlerV2Service, SelectOption, SortDirection } from '@gea/digital-ui-lib';
import { catchError, filter, first, of } from 'rxjs';

import { FaultsApiService, QueryParameterService } from '@shared/services';
import {
  Annunciation,
  DEFAULT_FAULT_STATUS,
  FaultAnalysisDto,
  FaultAnalysisHistoryItem,
  FaultsStateModel,
  FaultStatus,
  FetchFaultAnalysisParams,
  FetchFaultCodesParams,
  SortAnnunciationsParams,
} from '@shared/models';
import { TroubleshootingStore } from '../troubleshooting';
import { SORT_COLUMN } from '../organizations/organizations.comparator';
import { FaultComparator } from './fault.comparator';
import { FAULT_ANALYSIS_SESSION_STORAGE_KEY } from '@tab-container/fault-analysis';

const INITIAL_STATE: FaultsStateModel = {};

const EMPTY_FAULT_ANALYSIS: FaultAnalysisDto = {
  machines: [],
  shutdowns: [],
  warnings: [],
  faultHistory: [],
};

const isRouterSettled = (event: unknown): boolean =>
  event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError;

@Injectable({ providedIn: 'root' })
export class FaultsStore {
  private readonly injector = inject(Injector);
  private readonly faultsApi = inject(FaultsApiService);
  private readonly errorHandler = inject(ErrorHandlerV2Service);
  private readonly queryParametersService = inject(QueryParameterService);
  private readonly router = inject(Router);

  private readonly state = signal<FaultsStateModel>(INITIAL_STATE);

  readonly isLoading = computed(() => this.state().isLoading);
  readonly faultyMachines = computed<SelectOption<number>[] | undefined>(() => this.state().faultyMachines);
  readonly faultCodes = computed<SelectOption[] | undefined>(() => this.state().faultCodes);
  readonly faultCodeGroups = computed<SelectOption[] | undefined>(() => this.state().faultCodeGroups);
  readonly selectedFaults = computed<number[] | undefined>(() => this.state().selectedFaults);
  readonly selectedDateRange = computed<number | undefined>(() => this.state().selectedDateRange);
  readonly selectedMachines = computed<number[] | undefined>(() => this.state().selectedMachines);
  readonly shutdowns = computed<Annunciation[] | undefined>(() => this.state().shutdowns);
  readonly warnings = computed<Annunciation[] | undefined>(() => this.state().warnings);
  readonly faultHistory = computed<FaultAnalysisHistoryItem[] | undefined>(() => this.state().faultHistory);
  readonly selectedFaultStatus = computed<FaultStatus[]>(() => this.state().selectedFaultStatus ?? DEFAULT_FAULT_STATUS);

  constructor() {
    this.router.events.pipe(filter(isRouterSettled), takeUntilDestroyed()).subscribe(() => {
      if (this.state().isLoading) {
        this.patch({ isLoading: false });
      }
    });
  }

  fetchFaultCodes({ machines, dateRange }: FetchFaultCodesParams): void {
    this.patch({
      faultCodes: undefined,
      faultCodeGroups: undefined,
    });

    this.faultsApi
      .fetchFaultCodes({ machines, dateRange })
      .pipe(
        catchError((error: ApiErrorResponse) => {
          if (error) {
            this.errorHandler.handleError(error);
          }

          return of({ faultCodeGroups: [], faultCodes: [] });
        }),
        first()
      )
      .subscribe(({ faultCodeGroups, faultCodes }) => {
        this.patch({
          faultCodes,
          faultCodeGroups,
          warnings: faultCodeGroups.length ? undefined : [],
          shutdowns: faultCodeGroups.length ? undefined : [],
        });

        this.setSelectedFaultAnalysisState({ selectedFaults: this.resolveSelectedFaults(faultCodeGroups) });
      });
  }

  fetchFaultAnalysis({
    machines,
    faults = [],
    dateRange,
    faultStatus = [FaultStatus.PENDING, FaultStatus.SOLVED],
  }: FetchFaultAnalysisParams): void {
    this.patch({
      shutdowns: undefined,
      warnings: undefined,
      faultHistory: undefined,
      faultyMachines: undefined,
      isLoading: true,
    });

    this.faultsApi
      .fetchFaultAnalysis({ machines, dateRange, faults, faultStatus })
      .pipe(
        // Error reporting is intentionally disabled: the API is called twice and the error would be shown twice.
        catchError(() => of(EMPTY_FAULT_ANALYSIS))
      )
      .subscribe((faultAnalysis: FaultAnalysisDto) => {
        const faultyMachines = faultAnalysis.machines.map(({ name, id: value }) => ({ name, value }));
        const shutdowns = this.sortByPendingCount(faultAnalysis.shutdowns);
        const warnings = this.sortByPendingCount(faultAnalysis.warnings);

        this.patch({
          faultHistory: faultAnalysis.faultHistory,
          faultyMachines,
          shutdowns,
          warnings,
          isLoading: false,
        });
      });
  }

  setSelectedFaultAnalysisState(params: FaultsStateModel): void {
    this.patch(params);

    const { selectedMachines } = params;

    if (selectedMachines) {
      this.setDefaultDateRangeAndFaultStatus();
      this.fetchFaultCodes({ machines: selectedMachines });
    }

    this.queryParametersService.setQueryParams({ key: FAULT_ANALYSIS_SESSION_STORAGE_KEY, queryParams: params });
  }

  resetSelectedFaultAnalysisState(): void {
    this.patch({
      selectedFaults: undefined,
      selectedMachines: undefined,
      selectedDateRange: undefined,
      selectedFaultStatus: undefined,
    });
  }

  clearFaults(): void {
    this.patch({
      faultCodes: undefined,
      faultCodeGroups: undefined,
      shutdowns: undefined,
      warnings: undefined,
      faultHistory: [],
      selectedFaults: undefined,
    });
    // Resolved lazily: TroubleshootingStore depends on FaultsStore, so an eager injection would form a cycle.
    this.injector.get(TroubleshootingStore).setFaultCode(undefined);
  }

  resetFaultAnalysis(): void {
    this.patch({
      shutdowns: [],
      warnings: [],
      faultHistory: [],
      selectedFaults: [],
    });
  }

  sortAnnunciations({ columnName, sortOrder, annunciation }: SortAnnunciationsParams): void {
    const data = this.state()[annunciation] as Annunciation[] | undefined;

    if (data) {
      this.patch({
        [annunciation]: this.sortFaults(data, columnName, sortOrder),
      });
    }
  }

  setDefaultDateRangeAndFaultStatus(): void {
    let selectedDateRange = 28;
    let selectedFaultStatus = [FaultStatus.PENDING];

    const storedData = sessionStorage.getItem(FAULT_ANALYSIS_SESSION_STORAGE_KEY);

    if (storedData) {
      try {
        const sessionData = JSON.parse(storedData);
        selectedDateRange = Number(sessionData?.selectedDateRange) || selectedDateRange;
        selectedFaultStatus = sessionData?.selectedFaultStatus || selectedFaultStatus;
      } catch {
        sessionStorage.removeItem(FAULT_ANALYSIS_SESSION_STORAGE_KEY);
      }
    }

    this.setSelectedFaultAnalysisState({ selectedDateRange, selectedFaultStatus });
  }

  private patch(partial: FaultsStateModel): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }

  private resolveSelectedFaults(faultCodeGroups: SelectOption[]): number[] {
    const availableFaults = faultCodeGroups.map(({ value }) => value);
    const availableFaultsSet = new Set<number>(availableFaults);

    const storedFaults = this.getSelectedFaultsForFaultAnalysis();
    // only preselect faults from session storage iff they are all present in API faults
    if (storedFaults.length && storedFaults.every((fault: number) => availableFaultsSet.has(fault))) {
      return storedFaults;
    }

    return availableFaults;
  }

  private sortByPendingCount(annunciations: Annunciation[]): Annunciation[] {
    return [...annunciations].sort(FaultComparator.by(SORT_COLUMN.COUNT_PENDING, SortDirection.DESCENDING));
  }

  private sortFaults(selectedFaults: Annunciation[], column: SORT_COLUMN, order: SortDirection): Annunciation[] {
    return [...selectedFaults].sort(FaultComparator.by(column, order));
  }

  private getSelectedFaultsForFaultAnalysis(): number[] {
    const storedFaults = sessionStorage.getItem(FAULT_ANALYSIS_SESSION_STORAGE_KEY);

    if (storedFaults) {
      try {
        const parsed = JSON.parse(storedFaults);
        return parsed.selectedFaults ? parsed.selectedFaults : [];
      } catch {
        sessionStorage.removeItem(FAULT_ANALYSIS_SESSION_STORAGE_KEY);
      }
    }

    return [];
  }
}
